import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PaperPlaneTilt, LockKey } from "@phosphor-icons/react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { TwoFactorPanel } from "../components/TwoFactorPanel";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginComplete } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form");
  const [preToken, setPreToken] = useState("");

  useEffect(() => {
    api.getInvite(token)
      .then(inv => { setInvite(inv); setName(inv.name || ""); })
      .catch(e => setLoadErr(fmtErr(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.acceptInvite(token, { name, password });
      setPreToken(r.pre_token);
      setStage("setup_2fa");
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-lg bg-zinc-950 flex items-center justify-center">
            <PaperPlaneTilt size={20} weight="fill" className="text-white" />
          </div>
          <span className="font-heading text-xl font-semibold tracking-tight">MediHub</span>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-7 shadow-sm" data-testid="invite-card">
          {loadErr && (
            <div className="text-center py-6">
              <p data-testid="invite-error" className="text-sm text-rose-600 mb-2">{loadErr}</p>
              <p className="text-xs text-zinc-500">Ask your admin to send you a fresh invitation.</p>
            </div>
          )}

          {!loadErr && !invite && <div className="text-sm text-zinc-400 text-center py-8">Checking your invitation…</div>}

          {invite && stage === "form" && (
            <>
              <h1 className="font-heading text-lg font-medium mb-1">Join the team</h1>
              <p className="text-sm text-zinc-500 mb-5">Create your account for <strong>{invite.email}</strong>.</p>
              <form onSubmit={submit} className="space-y-3">
                <label className="block">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Your name</span>
                  <input data-testid="invite-name" value={name} onChange={e => setName(e.target.value)} autoFocus
                    className="block w-full h-10 rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950" />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Password (min 8 characters)</span>
                  <input data-testid="invite-password" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password"
                    className="block w-full h-10 rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950" />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Confirm password</span>
                  <input data-testid="invite-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password"
                    className="block w-full h-10 rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950" />
                </label>
                {err && <p data-testid="invite-form-error" className="text-sm text-rose-600">{err}</p>}
                <button data-testid="invite-submit" type="submit" disabled={busy || password.length < 8 || !confirm}
                  className="w-full h-10 rounded-md bg-zinc-950 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                  {busy ? "Creating account…" : "Continue"}
                </button>
              </form>
            </>
          )}

          {invite && stage === "setup_2fa" && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <LockKey size={18} className="text-zinc-700" />
                <h1 className="font-heading text-lg font-medium">Secure your account</h1>
              </div>
              <p className="text-sm text-zinc-500 mb-5">Two-factor authentication is required for everyone.</p>
              <TwoFactorPanel
                stage="setup_2fa"
                preToken={preToken}
                email={invite.email}
                onSuccess={(tok, u) => { loginComplete(tok, u); navigate("/", { replace: true }); }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
